import { computed, Injectable, signal } from '@angular/core';
import { UserInfo } from './field-keys.constants';

/** Logged-in user and their functionality codes (from /auth/login or /auth/getSession). */
@Injectable({ providedIn: 'root' })
export class UserSessionService {
  private readonly user = signal<UserInfo | null>(null);
  private readonly functionalities = signal<ReadonlySet<string>>(new Set());

  readonly currentUser = this.user.asReadonly();
  readonly isLoggedIn = computed(() => this.user() !== null);
  readonly initials = computed(() => {
    const name = this.user()?.userName ?? '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
  });

  get currentUserValue(): UserInfo | null {
    return this.user();
  }

  setSession(userInfo: UserInfo, functionalities: string[]): void {
    this.user.set(userInfo);
    this.functionalities.set(new Set(functionalities ?? []));
  }

  updateUser(patch: Partial<UserInfo>): void {
    const current = this.user();
    if (current) this.user.set({ ...current, ...patch });
  }

  hasAccess(fncode: string): boolean {
    return this.functionalities().has(fncode);
  }

  hasAnyAccess(fncodes: readonly string[]): boolean {
    return fncodes.length === 0 || fncodes.some((c) => this.functionalities().has(c));
  }

  clear(): void {
    this.user.set(null);
    this.functionalities.set(new Set());
  }
}
